import React, { useCallback } from 'react'
import { InputProps, CheckboxProps, TextAreaProps, DropdownProps } from '../../types'
import './input-component.scss'
import { ERROR } from '../../utils/message'
import { StatusLabel } from '../StatusLabel'
import { concatClassName } from '../../utils/helpers'
import { ReportIcon } from '../Icon/icon-component'

const Input = ({
  type,
  labelText,
  name,
  value,
  handleChange,
  required,
  disabled = false,
  placeholder,
  errors,
  touched,
  infoText,
  classes,
  dataTestId,
  ...props
}: InputProps) => {
  const isError = errors && errors[name] && touched && touched[name]
  const getClassName = useCallback(() => {
    if (isError) return 'error-input input-field'
    return 'input-field'
  }, [isError])

  return (
    <>
      {labelText && <div className='input-label'> {labelText}</div>}
      <div className='input-field-wrapper'>
        <input
          name={name}
          value={value}
          onChange={handleChange}
          className={`${getClassName()} ${concatClassName(classes)}`}
          placeholder={placeholder}
          type={type}
          {...props}
          required={required}
          disabled={disabled}
          data-testid={dataTestId}
        />
        {isError && (
          <span className='input-icon'>
            <ReportIcon height='20' width='20' type={ERROR} />
          </span>
        )}
      </div>
      {isError && <StatusLabel type={ERROR} variant='basic' text={errors[name]} />}
      {!isError && infoText && <StatusLabel type='info' variant='basic' text={infoText} />}
    </>
  )
}

const CheckboxInput = ({
  name,
  labelText,
  value,
  handleChange,
  errors,
  touched,
  dataTestId,
}: CheckboxProps) => {
  const isError = errors && errors[name] && touched && touched[name]

  return (
    <>
      <div className='input-checkbox-container'>
        {
          <input
            className='input-checkbox'
            checked={value === 'true' ? true : false}
            name={name}
            type={'checkbox'}
            value={value}
            onChange={handleChange}
            data-testid={dataTestId}
          />
        }
        <span className='input-checkbox-label'> {labelText} </span>
      </div>
      {isError && <StatusLabel type={ERROR} variant='basic' text={errors[name]} />}
    </>
  )
}

const TextboxInput = ({
  labelText,
  name,
  value,
  handleChange,
  required,
  placeholder,
  errors,
  touched,
  infoText,
  dataTestId,
}: TextAreaProps) => {
  const isError = errors && errors[name] && touched && touched[name]
  const getClassName = useCallback(() => {
    if (isError) return 'error-input input-field'
    return 'input-field'
  }, [isError])

  return (
    <>
      {labelText && <div className='input-label'> {labelText}</div>}
      <div className='input-field-wrapper'>
        <textarea
          placeholder={placeholder}
          required={required}
          className={getClassName()}
          name={name}
          value={value}
          data-testid={dataTestId}
          onChange={handleChange}
        />
        {isError && (
          <span className='input-icon'>
            <ReportIcon height='20' width='20' type={ERROR} />
          </span>
        )}
      </div>
      {isError && <StatusLabel type={ERROR} variant='basic' text={errors[name]} />}
      {!isError && infoText && <StatusLabel type='info' variant='basic' text={infoText} />}
    </>
  )
}

const Dropdown = ({
  labelText,
  name,
  value,
  handleChange,
  options = [],
  required,
  disabled = false,
  errors,
  touched,
  infoText,
  classes,
  dataTestId,
  ...props
}: DropdownProps) => {
  const isError = errors && errors[name] && touched && touched[name]
  const getClassName = useCallback(
    () => (isError ? 'error-input input-field' : 'input-field'),
    [isError],
  )

  return (
    <>
      {labelText && <div className='input-label'>{labelText}</div>}
      <div className='input-field-wrapper'>
        <select
          name={name}
          value={value}
          onChange={handleChange}
          className={`${getClassName()} ${concatClassName(classes)}`}
          required={required}
          disabled={disabled}
          data-testid={dataTestId}
          {...props}
        >
          <option value=''>Select</option>
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        {isError && (
          <span className='input-icon'>
            <ReportIcon height='20' width='20' type={ERROR} />
          </span>
        )}
      </div>
      {isError && <StatusLabel type={ERROR} variant='basic' text={errors[name]} />}
      {!isError && infoText && <StatusLabel type='info' variant='basic' text={infoText} />}
    </>
  )
}

export { Input, CheckboxInput, TextboxInput, Dropdown }
